/**
 * Configuration API client hooks using TanStack Query
 * Provides hooks for model pricing management
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiOrigin } from '@/lib/config'
import { ApiError } from './client'
import type {
  ModelPricing,
  ModelPricingListResponse,
  CreateModelPricingRequest,
  UpdateModelPricingRequest
} from '@/types/pricing'
import { apiFetch } from './http'

/**
 * Get the orchestrator API base URL from config
 * Ensures trailing slash for correct URL resolution with new URL()
 */
function getOrchestratorUrl(): string {
  // Absolute, because the callers below build URLs with `new URL(path, base)` and a relative base throws.
  // Behind the BFF the configured origin is empty, which means *this* origin — same-origin is the point
  // (checklist D5), and an absolute URL to the page's own origin is still same-origin.
  return `${apiOrigin() || window.location.origin}/`
}

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query key factory for configuration
 */
export const configurationKeys = {
  all: ['configuration'] as const,
  modelPricing: () => ['configuration', 'modelPricing'] as const
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch model pricing list from orchestrator API
 */
async function fetchModelPricing(): Promise<ModelPricingListResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/configuration/model-pricing', baseUrl)

  const response = await apiFetch(url.toString())

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Create a new model pricing entry
 */
async function createModelPricing(
  data: CreateModelPricingRequest
): Promise<ModelPricing> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/configuration/model-pricing', baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data)
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Update an existing model pricing entry
 */
async function updateModelPricing(
  modelName: string,
  updates: UpdateModelPricingRequest
): Promise<ModelPricing> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/configuration/model-pricing', baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...updates, modelName })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Delete a model pricing entry
 */
async function deleteModelPricing(modelName: string): Promise<void> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/configuration/model-pricing', baseUrl)

  const response = await apiFetch(url.toString(), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ modelName })
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}

// ============================================================================
// React Query Hooks
// ============================================================================

/**
 * Hook to fetch model pricing configuration
 */
export function useModelPricing() {
  return useQuery({
    queryKey: configurationKeys.modelPricing(),
    queryFn: fetchModelPricing
  })
}

/**
 * Hook to create a new model pricing entry
 */
export function useCreateModelPricing() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createModelPricing,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: configurationKeys.modelPricing()
      })
    }
  })
}

/**
 * Hook to update a model pricing entry
 */
export function useUpdateModelPricing() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ modelName, updates }: { modelName: string; updates: UpdateModelPricingRequest }) =>
      updateModelPricing(modelName, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: configurationKeys.modelPricing()
      })
    }
  })
}

/**
 * Hook to delete a model pricing entry
 */
export function useDeleteModelPricing() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteModelPricing,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: configurationKeys.modelPricing()
      })
    }
  })
}
